import { createSlice } from '@reduxjs/toolkit';

/**
 * App slice - Centralized application state
 * Consolidates: country, selectedLocation, selectedOrganization, user profile status
 *
 * Onboarding Status Flow:
 * - profile_pending: User needs to fill out personal profile
 * - token_pending: Profile done, user needs to enter ADM- or INV- token
 * - org_setup: Admin token entered, user needs to create organization/locations
 * - complete: All onboarding steps done, full access granted
 */

// Onboarding status constants
export const ONBOARDING_STATUS = {
  PROFILE_PENDING: 'profile_pending',
  TOKEN_PENDING: 'token_pending',
  ORG_SETUP: 'org_setup',
  COMPLETE: 'complete',
};

const initialState = {
  // User profile state
  country: '',
  organizationId: null,
  onboardingStatus: ONBOARDING_STATUS.PROFILE_PENDING,

  // Token redemption data (set after successful token validation)
  redeemedToken: null, // { type: 'admin_license' | 'invitation', tokenId: string, ... }

  // Selection state (previously in localStorage)
  selectedLocation: localStorage.getItem('selectedLocation') || '',
  selectedOrganization: localStorage.getItem('selectedOrganization') || '',

  // Loading states
  isProfileLoading: true,
  isInitialized: false,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // Profile actions
    setCountry: (state, action) => {
      state.country = action.payload;
    },
    setOrganizationId: (state, action) => {
      state.organizationId = action.payload;
    },
    setOnboardingStatus: (state, action) => {
      state.onboardingStatus = action.payload;
    },
    setRedeemedToken: (state, action) => {
      state.redeemedToken = action.payload;
    },

    // Selection actions - also sync to localStorage for tab persistence
    // Note: Don't dispatch events here - do it in the component/hook after dispatching the action
    setSelectedLocation: (state, action) => {
      state.selectedLocation = action.payload;
      localStorage.setItem('selectedLocation', action.payload);
    },
    setSelectedOrganization: (state, action) => {
      state.selectedOrganization = action.payload;
      localStorage.setItem('selectedOrganization', action.payload);
    },

    // Loading states
    setProfileLoading: (state, action) => {
      state.isProfileLoading = action.payload;
    },
    setInitialized: (state, action) => {
      state.isInitialized = action.payload;
    },

    // Batch update for profile data (from API response)
    setProfileData: (state, action) => {
      const { country, organizationId, onboardingStatus } = action.payload;
      if (country !== undefined) state.country = country;
      if (organizationId !== undefined) state.organizationId = organizationId;
      if (onboardingStatus !== undefined) state.onboardingStatus = onboardingStatus;
      state.isProfileLoading = false;
      state.isInitialized = true;
    },

    // Reset state (on logout)
    resetAppState: (state) => {
      state.country = '';
      state.organizationId = null;
      state.onboardingStatus = ONBOARDING_STATUS.PROFILE_PENDING;
      state.redeemedToken = null;
      state.selectedLocation = '';
      state.selectedOrganization = '';
      state.isProfileLoading = false;
      state.isInitialized = false;
      localStorage.removeItem('selectedLocation');
      localStorage.removeItem('selectedOrganization');
    },
  },
});

// Actions
export const {
  setCountry,
  setOrganizationId,
  setOnboardingStatus,
  setRedeemedToken,
  setSelectedLocation,
  setSelectedOrganization,
  setProfileLoading,
  setInitialized,
  setProfileData,
  resetAppState,
} = appSlice.actions;

// Selectors
export const selectCountry = (state) => state.app.country;
export const selectOrganizationId = (state) => state.app.organizationId;
export const selectOnboardingStatus = (state) => state.app.onboardingStatus;
export const selectRedeemedToken = (state) => state.app.redeemedToken;
export const selectSelectedLocation = (state) => state.app.selectedLocation;
export const selectSelectedOrganization = (state) => state.app.selectedOrganization;
export const selectIsProfileLoading = (state) => state.app.isProfileLoading;
export const selectIsInitialized = (state) => state.app.isInitialized;

// Derived selectors for backward compatibility
export const selectHasFilledProfile = (state) =>
  state.app.onboardingStatus !== ONBOARDING_STATUS.PROFILE_PENDING;

export const selectHasSetupLocation = (state) =>
  state.app.onboardingStatus === ONBOARDING_STATUS.COMPLETE;

// Compound selectors
export const selectIsProfileComplete = (state) =>
  state.app.onboardingStatus === ONBOARDING_STATUS.COMPLETE;

export const selectNeedsOnboarding = (state) =>
  state.app.onboardingStatus !== ONBOARDING_STATUS.COMPLETE;

export default appSlice.reducer;
